// src/services/api.js
// Always call the backend proxy, never the SDA API directly (avoids CORS)

const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

async function getJSON(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json().catch(() => ({})) : {};
  if (!res.ok) {
    const msg = body?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body?.data ?? body;
}

async function postJSON(path, payload, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await res.json().catch(() => ({})) : {};
  if (!res.ok || body?.ok === false) {
    const msg = body?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body?.data ?? body;
}

// ---------- Hierarchy (proxied by backend)
export async function getConferences() {
  return getJSON("/api/hierarchy/conference");
}
export async function getStations() {
  return getJSON("/api/hierarchy/station");
}
export async function getDistricts() {
  return getJSON("/api/hierarchy/district");
}
export async function getChurches() {
  return getJSON("/api/hierarchy/church");
}

export async function getHierarchyAll() {
  const [confs, stations, districts, churches] = await Promise.all([
    getConferences(),
    getStations(),
    getDistricts(),
    getChurches(),
  ]);
  return { conferences: confs, stations, districts, churches };
}

// ---------- Tests (persist attempts; grading done on frontend)
export async function submitTestAttempt(
  { courseSlug, lessonNumber, score, totalQuestions, correctCount, passed, answers, durationSec },
  token
) {
  return postJSON(
    "/api/tests/log",
    {
      courseSlug,
      lessonNumber,
      score,
      totalQuestions,
      correctCount,
      passed,
      answers,
      ...(Number.isFinite(durationSec) ? { durationSec } : {}),
    },
    token
  );
}

// ---------- Progress
export async function markProgressVisit({ courseSlug, lessonNumber }, token) {
  return postJSON("/api/progress/visit", { courseSlug, lessonNumber }, token);
}

export async function markProgressComplete({ courseSlug, lessonNumber }, token) {
  return postJSON("/api/progress/complete", { courseSlug, lessonNumber }, token);
}

export { API_BASE };
